import React, { useState, useEffect } from 'react';
import * as prismic from '@prismicio/client';
import { asText, asHTML } from '@prismicio/client';
import { Box } from '@mui/material';
import { Observer } from 'mobx-react';

import { PrismicBanner } from './PrismicBanner';
import { localeStore } from 'stores';

// Types for the banner data from Prismic
interface PrismicRichTextSpan {
  start: number;
  end: number;
  type: string;
}

interface PrismicRichTextElement {
  type: string;
  text: string;
  spans: PrismicRichTextSpan[];
  direction?: string;
}

type RichTextField = PrismicRichTextElement[];

interface PrismicBannerData {
  title: RichTextField;
  body: RichTextField;
  background_color: string;
  text_color: string;
  active: boolean;
  targets: { app_id: string }[]
}

interface PrismicBannerDocument {
  id: string;
  uid: string | null;
  type: string;
  data: PrismicBannerData;
}

// Initialize Prismic client
const prismicClient = prismic.createClient(
  'athletopia',
//   {
//     accessToken: process.env.REACT_APP_PRISMIC_ACCESS_TOKEN,
//   }
);

// Map application locales to Prismic locale format
const LOCALE_MAPPING: { [key in availableLocales]?: string } = {
  en: 'en-gb',
  sv: 'sv-se', 
  el: 'el-gr'
};

export const PrismicBanners: React.FC = () => {
  return (
    <Observer>
      {() => {
        const [banners, setBanners] = useState<PrismicBannerDocument[]>([]);
        const [loading, setLoading] = useState<boolean>(true);
        const [error, setError] = useState<string | null>(null);
        
        const currentLocale = localeStore.locale;
        const prismicLocale = LOCALE_MAPPING[currentLocale] || 'en-gb';

        useEffect(() => {
          fetchBanners();
        }, [prismicLocale]);

        const fetchBanners = async () => {
          try {
            setLoading(true);
            setError(null);

            // Fetch all banners of type 'banners' that are active with the correct locale
            const response = await prismicClient.getAllByType('banners', {
              lang: prismicLocale,
              filters: [
                prismic.filter.at('my.banners.active', true)
              ],
              orderings: [
                { field: 'document.first_publication_date', direction: 'desc' }
              ]
            });
            const banners = response as unknown as PrismicBannerDocument[];
            setBanners(banners?.filter((b) => b.data.targets.some(t => t.app_id === 'admin-legacy')) || []);
          } catch (err) {
            console.error('Error fetching banners from Prismic:', err);
            setError('Failed to load banners');
          } finally {
            setLoading(false);
          }
        };

        // Helper function to convert rich text to plain text
        const convertRichTextToString = (richText: RichTextField): string => {
          if (!richText || richText.length === 0) {
            return '';
          }
          return asText(richText as any) || '';
        };

        // Helper function to convert rich text to HTML
        const convertRichTextToHTML = (richText: RichTextField): string => {
          if (!richText || richText.length === 0) {
            return '';
          }
          return asHTML(richText as any) || '';
        };

        if (loading) {
          return <div />
        }

        if (error) {
          return (
            <Box style={{ padding: '16px', color: 'red', textAlign: 'center' }}>
              {error}
            </Box>
          );
        }

        // Filter out banners that are not active
        const activeBanners = banners.filter(banner => banner.data.active);

        if (activeBanners.length === 0) {
          return null;
        }

        return (
          <Box style={{ width: '100%' }}>
            {activeBanners.map((banner) => (
              <PrismicBanner
                key={banner.id}
                title={convertRichTextToString(banner.data.title)}
                body={convertRichTextToHTML(banner.data.body)}
                backgroundColor={banner.data.background_color || '#007bff'}
                textColor={banner.data.text_color}
              />
            ))}
          </Box>
        );
      }}
    </Observer>
  );
};

export default PrismicBanners;
